import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const client = axios.create({ baseURL: BASE_URL });

// Attach JWT to every request. school_token and super_admin_token are
// mutually exclusive in practice — only one is ever set, since "/" (App.jsx)
// and "/super-admin" (SuperAdminApp.jsx) are separate apps with separate
// logins — so checking both here is safe and lets this one client instance
// serve both without needing to know which app is currently mounted.
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('school_token') || localStorage.getItem('super_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// An expired/invalid JWT would otherwise fail every request with a generic
// "Failed to load" and leave the admin staring at a silently broken screen —
// force back to the login form instead.
client.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 from a login attempt just means wrong credentials — let the login
    // form show the server's message instead of reloading the page.
    const url = error.config?.url || '';
    const isLoginRequest = /^\/(auth\/|school-admins\/login|super-admin\/)/.test(url);
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('school_token');
      localStorage.removeItem('school_name');
      localStorage.removeItem('super_admin_token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// ── Auth ────────────────────────────────────────────────────
export const loginSchool = (code, password) =>
  client.post('/auth/school', { code, password }).then(r => r.data);

// ── Schools (super admin — auth via stored JWT, same interceptor as everything else) ─
export const getSchools   = ()         => client.get('/schools').then(r => r.data);
export const createSchool = (data)     => client.post('/schools', data).then(r => r.data);
export const updateSchool = (id, data) => client.put(`/schools/${id}`, data).then(r => r.data);
export const suspendSchool = (id, suspended) =>
  client.patch(`/schools/${id}/suspend`, { suspended }).then(r => r.data);
export const sendPaymentReminder = (id, message) =>
  client.post(`/schools/${id}/payment-reminder`, { message }).then(r => r.data);

// ── My School (logged-in school/admin — self-service profile) ─
export const getMySchool    = ()     => client.get('/school/me').then(r => r.data);
export const updateMySchool = (data) => client.put('/school/me', data).then(r => r.data);

// ── Students ────────────────────────────────────────────────
export const getStudents   = ()         => client.get('/students').then(r => r.data);
export const createStudent = (data)     => client.post('/students', data).then(r => r.data);
export const updateStudent = (id, data) => client.put(`/students/${id}`, data).then(r => r.data);
export const deleteStudent = (id)       => client.delete(`/students/${id}`).then(r => r.data);
export const getStudentProfile = (id)     => client.get(`/students/${id}/profile`).then(r => r.data);
export const searchStudents    = (q)      => client.get('/students/search', { params: { q } }).then(r => r.data);
export const uploadStudentPhoto = (id, data_url) => client.put(`/students/${id}/photo`, { data_url }).then(r => r.data);
export const deleteStudentPhoto = (id)  => client.delete(`/students/${id}/photo`).then(r => r.data);
export const getIdCards        = (params) => client.get('/students/id-cards', { params }).then(r => r.data);
export const getStudentAttendanceMonth = (id, month) =>
  client.get(`/attendance/history/${id}`, { params: { month } }).then(r => r.data);
export const searchGuardians = (q) => client.get('/guardians/search', { params: { q } }).then(r => r.data);

// ── Fees ────────────────────────────────────────────────────
export const getFeesSummary = () => client.get('/fees/summary').then(r => r.data);
export const getStudentFees    = (id)       => client.get(`/fees/student/${id}`).then(r => r.data);
export const setStudentFee     = (id, data) => client.put(`/fees/student/${id}`, data).then(r => r.data);
export const setFeeMonth       = (id, data) => client.put(`/fees/student/${id}/month`, data).then(r => r.data);
export const payFeeMonth       = (id, y, m) => client.post(`/fees/student/${id}/month/${y}/${m}/pay`).then(r => r.data);
export const unpayFeeMonth     = (id, y, m) => client.post(`/fees/student/${id}/month/${y}/${m}/unpay`).then(r => r.data);
export const sendFeeReminder   = (id)       => client.post(`/fees/student/${id}/remind`).then(r => r.data);

// ── Guardians ───────────────────────────────────────────────
export const getGuardians   = ()         => client.get('/guardians').then(r => r.data);
export const createGuardian = (data)     => client.post('/guardians', data).then(r => r.data);
export const updateGuardian = (id, data) => client.put(`/guardians/${id}`, data).then(r => r.data);

// ── Vans ────────────────────────────────────────────────────
export const getVans   = ()         => client.get('/vans').then(r => r.data);
export const createVan = (data)     => client.post('/vans', data).then(r => r.data);
export const updateVan = (id, data) => client.put(`/vans/${id}`, data).then(r => r.data);

// ── Drivers ─────────────────────────────────────────────────
export const getDrivers   = ()         => client.get('/drivers').then(r => r.data);
export const createDriver = (data)     => client.post('/drivers', data).then(r => r.data);
export const updateDriver = (id, data) => client.put(`/drivers/${id}`, data).then(r => r.data);

// ── Routes ──────────────────────────────────────────────────
export const getRoutes      = ()         => client.get('/routes').then(r => r.data);
export const createRoute    = (data)     => client.post('/routes', data).then(r => r.data);
export const updateRoute    = (id, data) => client.put(`/routes/${id}`, data).then(r => r.data);
export const getRouteStops  = (id)       => client.get(`/routes/${id}/stops`).then(r => r.data);

// ── Stops ───────────────────────────────────────────────────
export const getStops   = ()         => client.get('/stops').then(r => r.data);
export const createStop = (data)     => client.post('/stops', data).then(r => r.data);
export const updateStop = (id, data) => client.put(`/stops/${id}`, data).then(r => r.data);
export const deleteStop = (id)       => client.delete(`/stops/${id}`).then(r => r.data);

// ── SOS Alerts ──────────────────────────────────────────────
export const getSosAlerts   = (status = 'all') => client.get('/sos', { params: { status } }).then(r => r.data);
export const acknowledgeSos = (id)              => client.patch(`/sos/${id}/acknowledge`).then(r => r.data);
export const resolveSos     = (id)              => client.patch(`/sos/${id}/resolve`).then(r => r.data);

// ── School Admin accounts (super-admin only — auth via stored JWT) ──
export const loginSchoolAdmin  = (school_code, login_id, password) =>
  client.post('/school-admins/login', { school_code, login_id, password }).then(r => r.data);
export const getSchoolAdmins   = ()         => client.get('/school-admins').then(r => r.data);
export const createSchoolAdmin = (data)     => client.post('/school-admins', data).then(r => r.data);
export const updateSchoolAdmin = (id, data) => client.put(`/school-admins/${id}`, data).then(r => r.data);
export const deleteSchoolAdmin = (id)       => client.delete(`/school-admins/${id}`).then(r => r.data);

// ── Super admin login (email verification code — routes/superAdminAuth.js) ──
export const requestSuperAdminCode = (email) =>
  client.post('/super-admin/request-code', { email }).then(r => r.data);
export const verifySuperAdminCode  = (email, code) =>
  client.post('/super-admin/verify-code', { email, code }).then(r => r.data);

// ── Staff ───────────────────────────────────────────────────
export const getStaff    = ()         => client.get('/staff').then(r => r.data);
export const createStaff = (data)     => client.post('/staff', data).then(r => r.data);
export const updateStaff = (id, data) => client.put(`/staff/${id}`, data).then(r => r.data);

// ── Classes ─────────────────────────────────────────────────
export const getClasses      = ()         => client.get('/classes').then(r => r.data);
export const createClass     = (data)     => client.post('/classes', data).then(r => r.data);
export const updateClass     = (id, data) => client.put(`/classes/${id}`, data).then(r => r.data);
export const getClassRoster  = (id)       => client.get(`/classes/${id}/roster`).then(r => r.data);

// ── Class (student) attendance ─────────────────────────────
export const submitClassAttendance = (classId, records, override = false) =>
  client.post(`/class-attendance/${classId}/attendance`, { records, override }).then(r => r.data);

// ── Staff attendance ────────────────────────────────────────
export const getStaffAttendanceToday   = ()             => client.get('/staff-attendance/today').then(r => r.data);
export const getStaffAttendanceSummary = (staffId, month) =>
  client.get(`/staff-attendance/summary/${staffId}`, { params: { month } }).then(r => r.data);
export const getStaffAttendanceDashboard = () => client.get('/staff-attendance/dashboard').then(r => r.data);

// ── Students attendance summary (pie chart) ────────────────
export const getStudentsAttendanceSummary = () =>
  client.get('/students/attendance-summary/today').then(r => r.data);

// ── Announcements ───────────────────────────────────────────
export const getAnnouncements   = ()     => client.get('/announcements').then(r => r.data);
export const createAnnouncement = (data) => client.post('/announcements', data).then(r => r.data);

// ── Leave Requests ──────────────────────────────────────────
export const getLeaveRequests  = (status) => client.get('/leave-requests', { params: status ? { status } : {} }).then(r => r.data);
export const decideLeaveRequest = (id, status) => client.patch(`/leave-requests/${id}`, { status }).then(r => r.data);

// ── Timetable ───────────────────────────────────────────────
export const getTimetableSlots = (classId) => client.get('/timetable/slots', { params: classId ? { class_id: classId } : {} }).then(r => r.data);
export const upsertTimetableSlot = (data)   => client.post('/timetable/slots', data).then(r => r.data);
export const deleteTimetableSlot = (id)     => client.delete(`/timetable/slots/${id}`).then(r => r.data);
export const setSubstitute       = (slotId, date, substitute_staff_id) =>
  client.post(`/timetable/slots/${slotId}/substitute`, { date, substitute_staff_id }).then(r => r.data);

// ── Holidays ────────────────────────────────────────────────
export const getHolidays   = ()     => client.get('/holidays').then(r => r.data);
export const createHoliday = (data) => client.post('/holidays', data).then(r => r.data);
export const deleteHoliday = (id)   => client.delete(`/holidays/${id}`).then(r => r.data);

// ── Attendance Reports (date/month/year, students + staff) ────
export const getStudentAttendanceReport = (params) => client.get('/attendance-reports/students', { params }).then(r => r.data);
export const getStaffAttendanceReport   = (params) => client.get('/attendance-reports/staff', { params }).then(r => r.data);
export const exportStudentAttendance    = (params) => client.get('/attendance-reports/students/export', { params, responseType: 'blob' }).then(r => r.data);
export const exportStaffAttendance      = (params) => client.get('/attendance-reports/staff/export', { params, responseType: 'blob' }).then(r => r.data);

// ── Home dashboard ──────────────────────────────────────────
export const getDashboard = () => client.get('/dashboard').then(r => r.data);

// ── Fee reports ─────────────────────────────────────────────
export const getFeeCollectionReport = (params) => client.get('/reports/fee-collection', { params }).then(r => r.data);
export const getDuesReport          = (params) => client.get('/reports/dues', { params }).then(r => r.data);

// ── Admissions enquiries ────────────────────────────────────
export const getEnquiries   = (status)   => client.get('/enquiries', { params: status ? { status } : {} }).then(r => r.data);
export const createEnquiry  = (data)     => client.post('/enquiries', data).then(r => r.data);
export const updateEnquiry  = (id, data) => client.put(`/enquiries/${id}`, data).then(r => r.data);
export const deleteEnquiry  = (id)       => client.delete(`/enquiries/${id}`).then(r => r.data);

// ── Exams & results ─────────────────────────────────────────
export const getClassExams     = (classId)         => client.get(`/exam-results/exams/${classId}`).then(r => r.data);
export const getExamGrid       = (classId, examId) => client.get(`/exam-results/class/${classId}/exam/${examId}`).then(r => r.data);
export const saveExamMarks     = (classId, data)   => client.post(`/exam-results/class/${classId}/bulk`, data).then(r => r.data);
export const deleteExam        = (examId)          => client.delete(`/exam-results/exam/${examId}`).then(r => r.data);
export const deleteExamSubjectResult = (examId, studentId, subject) =>
  client.delete(`/exam-results/exam/${examId}/student/${studentId}/subject/${encodeURIComponent(subject)}`).then(r => r.data);
export const getSubjects       = (classId)         => client.get('/subjects', { params: { class_id: classId } }).then(r => r.data);
export const addSubject        = (data)            => client.post('/subjects', data).then(r => r.data);
export const patchSubject      = (id, data)        => client.patch(`/subjects/${id}`, data).then(r => r.data);
export const getStudentResults = (studentId)       => client.get(`/exam-results/student/${studentId}`).then(r => r.data);

// ── Admit cards (exam datesheets) ───────────────────────────
export const getDatesheets   = (classId) => client.get(`/admit-cards/datesheets/${classId}`).then(r => r.data);
export const getDatesheet    = (id)      => client.get(`/admit-cards/datesheet/${id}`).then(r => r.data);
export const saveDatesheet   = (data)    => client.put('/admit-cards/datesheet', data).then(r => r.data);
export const deleteDatesheet = (id)      => client.delete(`/admit-cards/datesheet/${id}`).then(r => r.data);

// ── Notifications (targeted push) ───────────────────────────
export const getNotificationReach = () => client.get('/announcements/reach').then(r => r.data);

// ── Homework & copy checking ────────────────────────────────
export const getHomework        = (params)   => client.get('/homework', { params }).then(r => r.data);
export const createHomework     = (data)     => client.post('/homework', data).then(r => r.data);
export const updateHomework     = (id, data) => client.put(`/homework/${id}`, data).then(r => r.data);
export const deleteHomework     = (id)       => client.delete(`/homework/${id}`).then(r => r.data);
export const getHomeworkAttachment = (id)    => client.get(`/homework/${id}/attachment`).then(r => r.data);
export const getHomeworkChecks  = (id)       => client.get(`/homework/${id}/checks`).then(r => r.data);
export const saveHomeworkChecks = (id, data) => client.put(`/homework/${id}/checks`, data).then(r => r.data);

// ── Syllabus ────────────────────────────────────────────────
export const getSyllabus         = (classId)  => client.get('/syllabus', { params: { class_id: classId } }).then(r => r.data);
export const getSyllabusProgress = ()         => client.get('/syllabus/progress').then(r => r.data);
export const addSyllabus         = (data)     => client.post('/syllabus', data).then(r => r.data);
export const updateSyllabusTopic = (id, data) => client.patch(`/syllabus/${id}`, data).then(r => r.data);
export const deleteSyllabusTopic = (id)       => client.delete(`/syllabus/${id}`).then(r => r.data);

// ── Fee structure, concessions, refunds, late fine ──────────
export const getFeeStructure    = (classId)  => client.get('/fees/structure', { params: classId ? { class_id: classId } : {} }).then(r => r.data);
export const addFeeHead         = (data)     => client.post('/fees/structure', data).then(r => r.data);
export const deleteFeeHead      = (id)       => client.delete(`/fees/structure/${id}`).then(r => r.data);
export const applyFeeStructure  = (classId)  => client.post('/fees/structure/apply', { class_id: classId }).then(r => r.data);
export const setConcession      = (id, data) => client.put(`/fees/student/${id}/concession`, data).then(r => r.data);
export const getPaymentHistory  = (id)       => client.get(`/fees/student/${id}/payments`).then(r => r.data);
export const addRefund          = (id, data) => client.post(`/fees/student/${id}/refunds`, data).then(r => r.data);
export const applyLateFine      = (data)     => client.post('/fees/late-fine', data).then(r => r.data);
export const payFeeMonthWith    = (id, y, m, data) => client.post(`/fees/student/${id}/month/${y}/${m}/pay`, data).then(r => r.data);
export const getStudentServices = (id)       => client.get(`/fees/student/${id}/services`).then(r => r.data);
export const setStudentService  = (id, headId, data) => client.put(`/fees/student/${id}/services/${headId}`, data).then(r => r.data);
export const addFeeCharge       = (id, data) => client.post(`/fees/student/${id}/charges`, data).then(r => r.data);

// ── Student records (documents, remarks, leave) ─────────────
export const getStudentDocuments = (id)       => client.get(`/student-records/${id}/documents`).then(r => r.data);
export const getStudentDocument  = (docId)    => client.get(`/student-records/documents/${docId}`).then(r => r.data);
export const uploadStudentDocument = (id, data) => client.post(`/student-records/${id}/documents`, data).then(r => r.data);
export const deleteStudentDocument = (docId)  => client.delete(`/student-records/documents/${docId}`).then(r => r.data);
export const getStudentRemarks   = (id)       => client.get(`/student-records/${id}/remarks`).then(r => r.data);
export const addStudentRemark    = (id, data) => client.post(`/student-records/${id}/remarks`, data).then(r => r.data);
export const deleteStudentRemark = (remarkId) => client.delete(`/student-records/remarks/${remarkId}`).then(r => r.data);
export const getStudentLeaves    = (id)       => client.get(`/student-records/${id}/leaves`).then(r => r.data);
export const getAllStudentLeaves = (params)   => client.get('/student-records/leaves', { params }).then(r => r.data);
export const addStudentLeave     = (id, data) => client.post(`/student-records/${id}/leaves`, data).then(r => r.data);
export const decideStudentLeave  = (leaveId, data) => client.patch(`/student-records/leaves/${leaveId}`, data).then(r => r.data);
export const deleteStudentLeave  = (leaveId)  => client.delete(`/student-records/leaves/${leaveId}`).then(r => r.data);

// ── Admissions follow-ups & visitors ────────────────────────
export const getCallList         = ()         => client.get('/enquiries/call-list').then(r => r.data);
export const getEnquiryFollowups = (id)       => client.get(`/enquiries/${id}/followups`).then(r => r.data);
export const addEnquiryFollowup  = (id, data) => client.post(`/enquiries/${id}/followups`, data).then(r => r.data);
export const admitEnquiry        = (id, studentId) => client.post(`/enquiries/${id}/admit`, { student_id: studentId }).then(r => r.data);
export const getVisitors         = (date)     => client.get('/visitors', { params: date ? { date } : {} }).then(r => r.data);
export const addVisitor          = (data)     => client.post('/visitors', data).then(r => r.data);
export const checkoutVisitor     = (id)       => client.patch(`/visitors/${id}/checkout`).then(r => r.data);
export const deleteVisitor       = (id)       => client.delete(`/visitors/${id}`).then(r => r.data);

// ── More reports ────────────────────────────────────────────
export const getEnquiryReport    = (params)   => client.get('/reports/enquiries', { params }).then(r => r.data);
export const getFollowupReport   = ()         => client.get('/reports/followups').then(r => r.data);
export const getDirectorReport   = ()         => client.get('/reports/director').then(r => r.data);
export const getUdiseReport      = ()         => client.get('/reports/udise').then(r => r.data);

// ── Accounts ────────────────────────────────────────────────
export const getAccountEntries   = (params)   => client.get('/accounts', { params }).then(r => r.data);
export const getAccountSummary   = (params)   => client.get('/accounts/summary', { params }).then(r => r.data);
export const addAccountEntry     = (data)     => client.post('/accounts', data).then(r => r.data);
export const deleteAccountEntry  = (id)       => client.delete(`/accounts/${id}`).then(r => r.data);

// ── Library ─────────────────────────────────────────────────
export const getBooks            = (q)        => client.get('/library/books', { params: q ? { q } : {} }).then(r => r.data);
export const addBook             = (data)     => client.post('/library/books', data).then(r => r.data);
export const updateBook          = (id, data) => client.put(`/library/books/${id}`, data).then(r => r.data);
export const deleteBook          = (id)       => client.delete(`/library/books/${id}`).then(r => r.data);
export const getBookIssues       = (params)   => client.get('/library/issues', { params }).then(r => r.data);
export const issueBook           = (data)     => client.post('/library/issues', data).then(r => r.data);
export const returnBook          = (id, fine) => client.patch(`/library/issues/${id}/return`, { fine }).then(r => r.data);

// ── Hostel ──────────────────────────────────────────────────
export const getHostelRooms     = ()         => client.get('/hostel/rooms').then(r => r.data);
export const addHostelRoom      = (data)     => client.post('/hostel/rooms', data).then(r => r.data);
export const updateHostelRoom   = (id, data) => client.put(`/hostel/rooms/${id}`, data).then(r => r.data);
export const deleteHostelRoom   = (id)       => client.delete(`/hostel/rooms/${id}`).then(r => r.data);
export const getHostelResidents = (params)   => client.get('/hostel/residents', { params }).then(r => r.data);
export const allocateHostel     = (data)     => client.post('/hostel/allocate', data).then(r => r.data);
export const vacateHostel       = (id)       => client.patch(`/hostel/allocations/${id}/vacate`, {}).then(r => r.data);

// ── Inventory ───────────────────────────────────────────────
export const getInventoryItems  = (q)        => client.get('/inventory/items', { params: q ? { q } : {} }).then(r => r.data);
export const addInventoryItem   = (data)     => client.post('/inventory/items', data).then(r => r.data);
export const deleteInventoryItem = (id)      => client.delete(`/inventory/items/${id}`).then(r => r.data);
export const getInventoryTxns   = (id)       => client.get(`/inventory/items/${id}/txns`).then(r => r.data);
export const addInventoryTxn    = (id, data) => client.post(`/inventory/items/${id}/txns`, data).then(r => r.data);

// ── HR & Payroll ────────────────────────────────────────────
export const getSalaries        = ()         => client.get('/payroll/salaries').then(r => r.data);
export const saveSalary         = (id, data) => client.put(`/payroll/salaries/${id}`, data).then(r => r.data);
export const getPayslips        = (month)    => client.get('/payroll/payslips', { params: { month } }).then(r => r.data);
export const generatePayroll    = (data)     => client.post('/payroll/generate', data).then(r => r.data);
export const payPayslip         = (id, data) => client.patch(`/payroll/payslips/${id}/pay`, data).then(r => r.data);
export const unpayPayslip       = (id)       => client.patch(`/payroll/payslips/${id}/unpay`, {}).then(r => r.data);
export const adjustPayslip      = (id, data) => client.patch(`/payroll/payslips/${id}/adjust`, data).then(r => r.data);
export const deletePayslip      = (id)       => client.delete(`/payroll/payslips/${id}`).then(r => r.data);

// ── Bulk import ─────────────────────────────────────────────
export const downloadImportTemplate = () => client.get('/import/students/template', { responseType: 'blob' }).then(r => r.data);
export const previewImport  = (data) => client.post('/import/students/preview', data).then(r => r.data);
export const commitImport   = (data) => client.post('/import/students/commit', data).then(r => r.data);

// ── Academic years & promotion ──────────────────────────────
export const getSessions        = ()     => client.get('/sessions').then(r => r.data);
export const createSession      = (data) => client.post('/sessions', data).then(r => r.data);
export const setCurrentSession  = (id)   => client.patch(`/sessions/${id}/current`).then(r => r.data);
export const getPromotion       = (classId) => client.get('/sessions/promotion', { params: { class_id: classId } }).then(r => r.data);
export const promoteClass       = (data) => client.post('/sessions/promote', data).then(r => r.data);
export const getClassHistory    = (studentId) => client.get(`/sessions/history/${studentId}`).then(r => r.data);
export const getStudentsByStatus = (status) => client.get('/students', { params: { status } }).then(r => r.data);

// ── Certificates ────────────────────────────────────────────
export const getCertificates    = (params) => client.get('/certificates', { params }).then(r => r.data);
export const getCertificatePrefill = (studentId) => client.get(`/certificates/prefill/${studentId}`).then(r => r.data);
export const issueCertificate   = (data) => client.post('/certificates', data).then(r => r.data);
